/**
 * UnlinkedMentionsPanel - メモのタイトルを書いているのに、リンクしていない本文の一覧
 */

import { ChevronRight } from 'lucide-react'
import { Fragment, useEffect, useState } from 'react'
import { Link } from 'react-router'

import { getUnlinkedMentions, type UnlinkedMention } from '@/api/services/links'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { cn } from '@/lib/utils'
import { textDocumentPath } from '@/routes/paths'

type Props = {
  memoId: number
  /** 本文中で強調するメモのタイトル */
  title: string
}

/** snippet 中の word を太字にする（大文字小文字は区別しない） */
const Highlighted = ({ text, word }: { text: string; word: string }) => {
  const index = word ? text.toLowerCase().indexOf(word.toLowerCase()) : -1
  if (index < 0) return <>{text}</>
  return (
    <Fragment>
      {text.slice(0, index)}
      <mark className="rounded bg-amber-500/25 px-0.5 text-inherit">{text.slice(index, index + word.length)}</mark>
      {text.slice(index + word.length)}
    </Fragment>
  )
}

export const UnlinkedMentionsPanel = ({ memoId, title }: Props) => {
  const [open, setOpen] = useState(false)
  const [items, setItems] = useState<UnlinkedMention[]>([])

  useEffect(() => {
    let cancelled = false
    setItems([])
    getUnlinkedMentions(memoId)
      .then((list) => {
        if (!cancelled) setItems(list)
      })
      .catch((err) => console.warn('[unlinked-mentions] failed to load', err))
    return () => {
      cancelled = true
    }
  }, [memoId, title])

  if (items.length === 0) return null

  return (
    <Collapsible open={open} onOpenChange={setOpen} className="mx-6 mt-4 border-t pt-3 sm:mx-0">
      <CollapsibleTrigger className="flex cursor-pointer items-center gap-1.5 text-sm font-medium text-muted-foreground">
        <ChevronRight className={cn('size-4 transition-transform', open && 'rotate-90')} />
        リンクされていない言及
        <span className="text-xs font-normal">{items.length} 件</span>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <p className="mt-1 px-2 text-xs text-muted-foreground">
          このタイトルを書いている本文です。開いて「[[」でリンクすると、上の一覧に移ります
        </p>
        <ul className="mt-2 space-y-1">
          {items.map((item) => {
            const path = textDocumentPath(item.source_type, item.source_id)
            if (!path) return null
            return (
              <li key={`${item.source_type}:${item.source_id}`}>
                <Link to={path} className="flex flex-col rounded-md px-2 py-1.5 hover:bg-accent/60">
                  <span className="truncate text-sm">{item.title}</span>
                  <span className="line-clamp-2 text-xs text-muted-foreground">
                    <Highlighted text={item.snippet} word={title.trim()} />
                  </span>
                </Link>
              </li>
            )
          })}
        </ul>
      </CollapsibleContent>
    </Collapsible>
  )
}
